import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import {
  OVERRIDE_TYPES,
  type OverrideTypes,
  type OverrideVariant,
} from 'lumen/utils/constants/pricing-assistant-constants';

import styles from './price-preview.module.css';

interface HotelPricingAssistantTableModalsEditOverrideModalPricePreviewSignature {
  Args: {
    isLoadingSimulatedPrices?: boolean;
    overrideType: OverrideTypes;
    roomName: string;
    showOverrides: boolean;
    simulatedPrices: {
      formattedCurrentPrice: string | null;
      formattedOverriddenPrice: string | null;
      overrideVariant?: OverrideVariant;
      roomName: string;
      showOverride: boolean;
    }[];
  };
}

<template>
  <div class="divider-line" />
  <div class="flex flex-col gap-200">
    <p class={{styles.title}}>{{t
        "pricing_assistant.overrides.price_preview"
      }}</p>

    {{#if (eq @overrideType OVERRIDE_TYPES.DISCOUNT)}}
      <p class={{styles.description}}>{{t
          "pricing_assistant.overrides.price_preview.discount.description"
        }}</p>
    {{/if}}

    {{#if
      (or
        (eq @overrideType OVERRIDE_TYPES.EXACT_PRICE)
        (eq @overrideType OVERRIDE_TYPES.PRICE_RANGE)
      )
    }}
      <div class="flex items-center gap-100">
        <p class={{styles.description}}>{{t
            "pricing_assistant.overrides.price_preview.exact_price.description"
          }}</p>
        <LinkTo
          @route="hotel.settings.recommendations.price-range"
          class="text-200--medium text-neutral-default underline"
          data-test-id="price-range-settings-link"
          rel="noopener noreferrer"
          target="_blank"
        >
          <span class="flex items-center gap-50">
            {{t "menu.settings"}}
            <PrismIcon @name="link-out" @size="small" />
          </span>
        </LinkTo>
      </div>
    {{/if}}
  </div>

  <div class="flex flex-col gap-200">
    {{#each @simulatedPrices as |simulatedPrice|}}
      <div
        class="{{styles.room}}
          {{if (eq simulatedPrice.roomName @roomName) styles.highlighted}}
          {{if simulatedPrice.showOverride 'cursor-pointer'}}
          flex justify-between"
      >
        <p>{{simulatedPrice.roomName}}</p>

        <div class="flex items-center gap-100">
          {{#if @isLoadingSimulatedPrices}}
            <div class="loading-pulse w-[120px]"></div>
          {{else}}
            <span
              class={{if
                simulatedPrice.formattedOverriddenPrice
                "line-through"
              }}
            >
              {{simulatedPrice.formattedCurrentPrice}}
            </span>

            <div class="flex items-center gap-50">
              {{#if simulatedPrice.formattedOverriddenPrice}}
                {{! template-lint-disable no-bare-strings }}
                →
                {{simulatedPrice.formattedOverriddenPrice}}
              {{/if}}

              <div class={{if @showOverrides "w-400"}}>
                {{#if simulatedPrice.showOverride}}
                  <OverrideIndicator
                    @variant={{simulatedPrice.overrideVariant}}
                  />
                {{/if}}
              </div>
            </div>

            {{#if simulatedPrice.showOverride}}
              <PrismTooltip
                @side="left"
                @text={{t
                  "pricing_assistant.overrides.price_preview.override.tooltip"
                }}
              />
            {{/if}}
          {{/if}}
        </div>
      </div>
    {{/each}}
  </div>
</template> satisfies TOC<HotelPricingAssistantTableModalsEditOverrideModalPricePreviewSignature>;
